import { useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import {
  checkRoleMembership,
  getAllGroupMembers,
  UserKeys,
} from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import GeneralDialog from '../../../../../shared/general-dialog';
import SharedDialog from '../../../../../shared/shared-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import { UserRole } from '../../../../../../types';
import { useMemo, useRef, useState } from 'react';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { addRoleMembers } from '../../../../../api/users-api';
import { RoleMember } from '../../../../../../types';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../utils/message-constants';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete';
import { emailRegex } from '../../../../../utils/regular-expressions';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { createGridOverlays } from '../../../../grid-overlay/no-data-overlay-helpers';
import AvatarChip from '../../../../../shared/avatar-chip';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import RoleMembershipExpirationChip from './role-membership-expiration-chip';
import SharedDataGridClient from '../../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../../shared/shared-toolbar-latest';
import { isFeatureEnabled } from '../../../../../utils/feature-flags';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PersonOff from '@mui/icons-material/PersonOff';

function CustomToolbar() {
  return <SharedToolbar noColumn noFilter></SharedToolbar>;
}

interface AddRoleMemberDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRole: UserRole;
  changeLogRoles: string[];
  membersList: RoleMember[];
}

export default function AddRoleMemberDialog({
  open,
  handleClose,
  selectedRole,
  changeLogRoles,
  membersList,
}: AddRoleMemberDialogProps) {
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [membersWithPreviousRoles, setMembersWithPreviousRoles] = useState<
    (RoleMember & { previousRole?: string })[]
  >([]);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<
    (RoleMember & { sourceGroup?: string })[]
  >([]);
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
  const autocompleteValueRef = useRef('');
  const memberListEmails = membersList.map((member) =>
    member.userEmail.toLowerCase()
  );
  const { adminEmail, allDomains, primaryDomain, features, currentTier } =
    useUserSettings();
  const groupRolesEnabled = isFeatureEnabled(
    'groupsGovernanceBackend',
    features
  );
  const showEnterpriseGroupInfo =
    groupRolesEnabled && currentTier === 'enterprise';
  const roleQueries = useQueries({
    queries: selectedMembers.map((member) => ({
      queryKey: [
        UserKeys.RoleMembersRole,
        member.userEmail,
        member.memberType ?? 'USER',
      ],
      queryFn: () => checkRoleMembership(member.userEmail, member.memberType),
    })),
  });

  const roleQueriesLoading = roleQueries.map((query) => query.isLoading);
  const roleQueriesError = roleQueries.map((query) => query.isError);
  const roleQueriesSuccess = roleQueries.map((query) => query.isSuccess);
  const roleQueriesData = roleQueries.map((query) => query.data);

  const membersForGrid = useMemo(() => {
    return selectedMembers.map((member, index) => {
      const isLoading = roleQueriesLoading[index];
      const isError = roleQueriesError[index];
      const isSuccess = roleQueriesSuccess[index];
      const data = roleQueriesData[index];

      let previousRole: string | undefined;

      if (isLoading) {
        previousRole = undefined;
      } else if (isError) {
        previousRole = 'Error fetching role';
      } else if (isSuccess) {
        previousRole = data ?? '';
      }

      return {
        ...member,
        previousRole,
      };
    });
  }, [
    selectedMembers,
    roleQueriesLoading,
    roleQueriesError,
    roleQueriesSuccess,
    roleQueriesData,
  ]);

  const { mutate: fetchGroupMembers, isPending: isFetchingGroup } = useMutation(
    {
      mutationFn: (groupEmail: string) => getAllGroupMembers(groupEmail),
      onSuccess: (groupMembers, groupEmail) => {
        const adminWasInGroup = groupMembers.some(
          (member) =>
            member.type === 'user' &&
            member.email.toLowerCase() === adminEmail.toLowerCase()
        );

        if (adminWasInGroup && selectedRole.roleName !== 'Admin') {
          setInfoMessage(
            `${adminEmail} is the gPanel admin and can only be assigned the Admin role.`
          );
        }
        const potentialMembers = groupMembers.filter((member) => {
          const domain = member.email.split('@')[1];
          const isDomainValid =
            allDomains.includes(domain) || domain === primaryDomain;
          return (
            member.type === 'user' &&
            isDomainValid &&
            !memberListEmails.includes(member.email.toLowerCase()) &&
            !(
              selectedRole.roleName !== 'Admin' &&
              member.email.toLowerCase() === adminEmail.toLowerCase()
            )
          );
        });

        const updatedMembers = [...selectedMembers];
        potentialMembers.forEach((member) => {
          const existingMemberIndex = updatedMembers.findIndex(
            (sm) => sm.userEmail.toLowerCase() === member.email.toLowerCase()
          );

          if (existingMemberIndex !== -1) {
            const existingMember = updatedMembers[existingMemberIndex];
            const sources = new Set(
              existingMember.sourceGroup?.split(', ') ?? []
            );
            sources.add(groupEmail);
            updatedMembers[existingMemberIndex] = {
              ...existingMember,
              sourceGroup: Array.from(sources).join(', '),
            };
          } else {
            updatedMembers.push({
              userEmail: member.email,
              role: selectedRole.roleName,
              isAppAdmin: false,
              sourceGroup: groupEmail,
            });
          }
        });

        setSelectedMembers(updatedMembers);
        setSelectionModel(updatedMembers.map((m) => m.userEmail));
        setErrorMessage('');
        setAutoCompleteResponse('');
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage: 'Could not fetch members for the selected group.',
        });
      },
    }
  );

  const sourceGroups = [
    ...new Set(
      selectedMembers.flatMap((m) => m.sourceGroup?.split(', ') ?? [])
    ),
  ];

  const selectedMemberEmails = new Set(
    selectedMembers.map((member) => member.userEmail.toLowerCase())
  );

  const selectedGroupEmails = new Set(
    selectedMembers
      .filter((member) => member.memberType === 'GROUP')
      .map((member) => member.userEmail.toLowerCase())
  );

  function addGroupSelection(groupEmail: string) {
    const domain = groupEmail.split('@')[1];
    const isDomainValid =
      allDomains.includes(domain) || domain === primaryDomain;

    if (!emailRegex.test(groupEmail)) {
      setErrorMessage('Please enter a valid email address');
    } else if (!isDomainValid) {
      setErrorMessage('Only groups from your domains can be added.');
    } else if (memberListEmails.includes(groupEmail.toLowerCase())) {
      setErrorMessage('Group is already a member of this role.');
    } else if (selectedMemberEmails.has(groupEmail.toLowerCase())) {
      setErrorMessage('Group has already been selected.');
    } else {
      setErrorMessage('');
      const newGroupMember: RoleMember & { sourceGroup?: string } = {
        userEmail: groupEmail,
        role: selectedRole.roleName,
        isAppAdmin: false,
        memberType: 'GROUP',
        sourceGroup: groupEmail,
      };
      const newMembers = [...selectedMembers, newGroupMember];
      setSelectedMembers(newMembers);
      setSelectionModel(newMembers.map((m) => m.userEmail));
      setAutoCompleteResponse('');
    }
  }

  async function addSelection(email: string, memberType: string) {
    if (memberType === 'user') {
      if (
        selectedRole.roleName !== 'Admin' &&
        email.toLowerCase() === adminEmail.toLowerCase()
      ) {
        setErrorMessage(
          `${email} is the gPanel admin and can only be assigned the Admin role.`
        );
      } else if (!emailRegex.test(email)) {
        setErrorMessage('Please enter a valid email address');
      } else if (memberListEmails.includes(email.toLowerCase())) {
        setErrorMessage('User is already a member of this role.');
      } else if (
        selectedMembers.some(
          (member) => member.userEmail.toLowerCase() === email.toLowerCase()
        )
      ) {
        setErrorMessage('User has already been selected.');
      } else {
        setErrorMessage('');
        const newRoleMember: RoleMember & { sourceGroup?: string } = {
          userEmail: email,
          role: selectedRole.roleName,
          isAppAdmin: false,
          sourceGroup: email,
        };
        const newMembers = [...selectedMembers, newRoleMember];
        setSelectedMembers(newMembers);
        setSelectionModel(newMembers.map((m) => m.userEmail));
        setAutoCompleteResponse('');
      }
    } else if (groupRolesEnabled) {
      addGroupSelection(email);
    } else {
      fetchGroupMembers(email);
    }
  }

  const handleAutocompleteValueChange = (newValue: string | null) => {
    const value = newValue || '';
    autocompleteValueRef.current = value;
    setAutoCompleteResponse(value);
    setErrorMessage('');
    setInfoMessage('');
  };

  const handleAutocompleteTypeChange = (newType: string) => {
    const value = autocompleteValueRef.current;

    if (newType && value) {
      addSelection(value, newType);
    }
  };

  const finalMembersToAdd = membersForGrid.filter((member) =>
    selectionModel.includes(member.userEmail)
  );

  const { mutate, isPending } = useMutation({
    mutationFn: (members: RoleMember[]) =>
      addRoleMembers(
        members.map(
          ({ userEmail, role, isAppAdmin, expiryTime, memberType }) => ({
            userEmail,
            role,
            isAppAdmin,
            expiryTime,
            ...(memberType && { memberType }),
          })
        ),
        changeLogMessage
      ),
    onSuccess: (returnVal, data) => {
      if (returnVal.status === 'SUCCESS') {
        setSnackbar({
          open: true,
          severity: 'success',
          message: Messages.Success.Added(
            `${returnVal.succeeded.length} ${
              returnVal.succeeded.length === 1 ? 'member' : 'members'
            }`
          ),
        });
      } else {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: Messages.Errors.Network.Adding(
            `${returnVal.failed.join(', ')} to ${selectedRole.roleName}`
          ),
        });
      }

      queryClient.setQueryData<RoleMember[]>(
        [UserKeys.RoleMembers, selectedRole.roleName],
        (oldData) => {
          if (!oldData) return;
          return [
            ...oldData,
            ...data.filter((member) =>
              returnVal.succeeded.includes(member.userEmail)
            ),
          ];
        }
      );
      setAutoCompleteResponse('');
      setSelectedMembers([]);
      setSelectionModel([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Adding(
          'the selected members to the role'
        ),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.role_details.add_role_members, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.roleManager,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const proceedWithAddingMembers = () => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.AddMemberToRole.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(finalMembersToAdd);
    }
  };

  function handleSubmit() {
    const membersToConfirm = finalMembersToAdd.filter(
      (member) =>
        member.previousRole && member.previousRole !== 'Error fetching role'
    );
    if (membersToConfirm.length > 0) {
      setMembersWithPreviousRoles(membersToConfirm);
      setOpenConfirmationDialog(true);
    } else {
      proceedWithAddingMembers();
    }
  }

  function removeBySource(sourceToRemove: string) {
    const newSelectedMembers = selectedMembers
      .map((member) => {
        if (!member.sourceGroup) return member;

        const sources = member.sourceGroup.split(', ');
        if (!sources.includes(sourceToRemove)) return member;

        const newSources = sources.filter((s) => s !== sourceToRemove);
        if (newSources.length === 0) {
          return null;
        }

        return { ...member, sourceGroup: newSources.join(', ') };
      })
      .filter(
        (member): member is RoleMember & { sourceGroup?: string } =>
          member !== null
      );

    setSelectedMembers(newSelectedMembers);
    setSelectionModel(newSelectedMembers.map((m) => m.userEmail));
  }

  const columns: GridColDef[] = [
    {
      field: 'userEmail',
      headerName: 'Email',
      flex: 1,
      pinnable: false,
    },
    {
      field: 'previousRole',
      headerName: 'Previous Role',
      flex: 1,
      renderCell: (params) => {
        const { value } = params;
        const randomWidth = 100 + Math.random() * 40;
        if (value === undefined) {
          return (
            <Stack
              display={'flex'}
              justifyContent={'center'}
              width={'100%'}
              height={'100%'}
            >
              <Skeleton height={30} width={randomWidth} variant="text" />
            </Stack>
          );
        }
        if (params.row.memberType === 'GROUP') {
          return value || 'None';
        }
        return value || 'No previous role';
      },
    },
    {
      field: 'expiryTime',
      headerName: 'Expiration',
      flex: 1,
      renderCell: (params) => {
        if (
          selectedRole.roleName === 'Admin' &&
          params.row.userEmail.toLowerCase() === adminEmail.toLowerCase()
        ) {
          return (
            <Tooltip title="The gPanel admin cannot have an expiry date">
              <span>
                <RoleMembershipExpirationChip
                  currentExpirationTime={params.row.expiryTime}
                  disabled={true}
                  onExpirationChange={() => {}}
                />
              </span>
            </Tooltip>
          );
        }

        return (
          <RoleMembershipExpirationChip
            currentExpirationTime={params.row.expiryTime}
            onExpirationChange={(newExpirationTime) => {
              const updatedMembers = selectedMembers.map((m) =>
                m.userEmail === params.row.userEmail
                  ? { ...m, expiryTime: newExpirationTime || undefined }
                  : m
              );
              setSelectedMembers(updatedMembers);
            }}
          />
        );
      },
    },
  ];
  columns.push({
    field: 'sourceGroup',
    headerName: 'Source',
    flex: 1,
    valueGetter: (_, row) => {
      if (!row.sourceGroup) {
        return 'Directly added';
      }
      const sources = row.sourceGroup.split(', ');
      return sources
        .map((source: string) =>
          source === row.userEmail ? 'Directly added' : source
        )
        .join(', ');
    },
  });

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        setAutoCompleteResponse('');
        setErrorMessage('');
        setSelectedMembers([]);
        setInfoMessage('');
        setSelectionModel([]);
        handleClose();
      }}
      title="Add Members"
      maxWidth="lg"
      dialogContent={
        <Stack gap={1}>
          <Typography variant="body2">
            {groupRolesEnabled ? (
              <>
                If you select a group, the group itself will be added to{' '}
                {selectedRole.roleName} and its members will receive the
                role&apos;s permissions.
              </>
            ) : (
              <>
                If you select a group, all internal members of that group will
                be available to be added to {selectedRole.roleName}. Subgroups
                of that group will be ignored.
              </>
            )}
          </Typography>
          <Stack paddingTop={1}>
            <SharedAutocomplete
              freeSolo
              sources={['users', 'groups']}
              disabled={isFetchingGroup}
              autocompleteValue={autoCompleteResponse}
              setAutoCompleteValue={handleAutocompleteValueChange}
              setAutoCompleteTypeValue={handleAutocompleteTypeChange}
              errors={!!errorMessage}
              helperText={
                isFetchingGroup ? 'Adding group members...' : errorMessage
              }
            />
            {sourceGroups.length > 0 && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{
                  flexWrap: 'nowrap',
                  borderRadius: 1,
                  maxHeight: 100,
                  overflowX: 'auto',
                  py: 1,
                }}
              >
                {sourceGroups.map((group) => {
                  const isDirectAddSource =
                    selectedMemberEmails.has(group.toLowerCase()) &&
                    !selectedGroupEmails.has(group.toLowerCase());
                  const chipType = isDirectAddSource ? 'User' : 'Group';
                  return (
                    <AvatarChip
                      key={group}
                      type={chipType}
                      email={group}
                      label={group}
                      onDelete={() => removeBySource(group)}
                      size="small"
                    />
                  );
                })}
              </Stack>
            )}
            {infoMessage && (
              <Alert severity="info" sx={{ alignItems: 'center' }}>
                {infoMessage}
              </Alert>
            )}
            {showEnterpriseGroupInfo && (
              <Alert
                severity="info"
                variant="standard"
                sx={(theme) => ({
                  mt: 1.5,
                  backgroundColor: theme.palette.colors.info[100],
                  color: theme.palette.colors.darkBlue[4],
                  '& .MuiAlert-icon': {
                    color: theme.palette.colors.info[600],
                  },
                  ...theme.applyStyles('dark', {
                    backgroundColor: theme.palette.colors.info[200],
                    color: theme.palette.colors.darkBlue[5],
                    '& .MuiAlert-icon': {
                      color: theme.palette.colors.info[700],
                    },
                  }),
                })}
              >
                On your <strong>Enterprise plan</strong>, groups stay as a
                single entry instead of splitting into a long list of individual
                users. Everyone inside still gets access automatically.
              </Alert>
            )}
          </Stack>
          <Box sx={{ height: '50vh', width: '100%', mt: 1 }}>
            <SharedDataGridClient
              density="compact"
              checkboxSelection
              selectedRows={selectionModel}
              setSelectedRows={setSelectionModel}
              slots={{
                toolbar: CustomToolbar,
                ...createGridOverlays(
                  '',
                  <PersonOff />,
                  groupRolesEnabled
                    ? 'Selected users and groups will appear here.'
                    : 'Users and members of selected groups will appear here.',
                  'No Members selected'
                ),
              }}
              localeText={{
                footerRowSelected: (count) =>
                  `${count.toLocaleString()} member${
                    count > 1 ? 's' : ''
                  } will be added`,
              }}
              getRowId={(row) => row.userEmail}
              columns={columns}
              rows={membersForGrid}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
              }}
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => mutate(finalMembersToAdd)}
            mutateLoading={isPending}
          />
          <SharedDialog
            open={openConfirmationDialog}
            handleClose={() => setOpenConfirmationDialog(false)}
            title="Confirm Role Change"
            dialogContent={
              <Stack gap={2}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography>
                    {membersWithPreviousRoles.length > 1 ? (
                      <>
                        The following{' '}
                        {membersWithPreviousRoles.some(
                          (member) => member.memberType === 'GROUP'
                        )
                          ? 'members'
                          : 'users'}{' '}
                        already have a role. Adding them to{' '}
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                          {selectedRole.roleName}
                        </Box>{' '}
                        will remove them from their current role.
                      </>
                    ) : (
                      <>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                          {membersWithPreviousRoles[0]?.userEmail}
                        </Box>{' '}
                        already has a role. Adding them to{' '}
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                          {selectedRole.roleName}
                        </Box>{' '}
                        will remove them from their current role.
                      </>
                    )}
                  </Typography>
                </Stack>
                <TableContainer
                  sx={{
                    maxHeight: '150px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Current Role</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {membersWithPreviousRoles.map((member) => (
                        <TableRow hover key={member.userEmail}>
                          <TableCell>{member.userEmail}</TableCell>
                          <TableCell>{member.previousRole}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography>Are you sure you want to continue?</Typography>
              </Stack>
            }
            primaryButton={{
              action: () => {
                setOpenConfirmationDialog(false);
                proceedWithAddingMembers();
              },
              title: 'Confirm Role Change',
              color: 'warning',
              loading: isPending,
              disabled: finalMembersToAdd.length === 0,
            }}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit,
        disabled: finalMembersToAdd.length === 0,
        title: 'Add',
      }}
    />
  );
}
